import { Legend } from "recharts";
import styled from "styled-components/macro";
import { FONT_SIZE_EXTRA_SMALL } from "../../../constants/constants";

const LEGEND_ICON_SIZE_PX = 10;
export const LEGEND_PADDING_PX = 8;
export const LEGEND_HEIGHT_PX = 25;

const RechartLegendStyles = styled.div`
  display: flex;
  justify-content: center;
  font-family: "Open Sans";
  font-size: ${FONT_SIZE_EXTRA_SMALL}px;

  .legendItem {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-right: ${LEGEND_PADDING_PX}px;

    .legendText {
      padding-left: ${LEGEND_PADDING_PX}px;
    }
  }

  @media print {
    width: fit-content;
    font-size: ${FONT_SIZE_EXTRA_SMALL - 2}px;
  }
`;

const LegendIcon = styled.div<{ color: string }>`
  /* We want the default shape (rect) and circle to show up as a circle */
  &.legend-circle,
  &.legend-rect {
    width: ${LEGEND_ICON_SIZE_PX}px;
    height: ${LEGEND_ICON_SIZE_PX}px;
    border-radius: 50%;
    background-color: ${props => props.color};
  }

  &.legend-line {
    width: ${LEGEND_ICON_SIZE_PX}px;
    border: 1px solid ${props => props.color};
  }
`;

export enum LegendIconEnum {
  circle = "circle",
  rect = "rect",
  line = "line",
}

export type LegendItemType = {
  color: string;
  type: keyof typeof LegendIconEnum;
  value: string;
};

/**
 * Loop through custom legend items
 *
 * @param {LegendItemType[]} legendItems
 * @return {*}
 */
export const LegendContent = ({
  legendItems,
}: {
  legendItems: LegendItemType[];
}) => {
  return (
    <RechartLegendStyles>
      {legendItems.map((entry, index) => {
        const className = `legend-${LegendIconEnum[entry.type]}`;

        return (
          entry.value && (
            <div key={`legendItem-${index}`} className="legendItem">
              <LegendIcon color={entry.color} className={className} />
              <div className="legendText">{entry.value}</div>
            </div>
          )
        );
      })}
    </RechartLegendStyles>
  );
};

// copied from recharts
type LayoutType = "horizontal" | "vertical" | "centric" | "radial";
type HorizontalAlignmentType = "center" | "left" | "right";
type VerticalAlignmentType = "top" | "bottom" | "middle";

export default function RechartLegend({
  width,
  align = "center",
  verticalAlign = "top",
  layout = "horizontal",
  customLegendItems,
}: {
  width: number;
  align?: HorizontalAlignmentType;
  verticalAlign?: VerticalAlignmentType;
  layout?: LayoutType;
  customLegendItems?: LegendItemType[];
}) {
  return (
    <Legend
      content={props => (
        <LegendContent
          legendItems={customLegendItems ?? (props.payload as any)}
        />
      )}
      height={LEGEND_HEIGHT_PX}
      wrapperStyle={{ top: LEGEND_PADDING_PX }}
      {...{
        width: width * 0.95,
        align,
        verticalAlign,
        layout,
      }}
    />
  );
}
